import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    Paper,
    IconButton,
    TextField,
    Snackbar,
    Alert,
    CircularProgress,
    Checkbox,
    InputAdornment,
} from "@mui/material";
import { Schedule, Done, Visibility, Add, Search } from "@mui/icons-material";
import api from "../../api";

export default function ParentInterviewManagement() {
    const [interviews, setInterviews] = useState([]);
    const [open, setOpen] = useState(false);
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [selectedInterview, setSelectedInterview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState(null);
    const [form, setForm] = useState({
        interview_date: "",
        start_time: "",
        end_time: "",
        venue: "",
    });
    const [passedApplicants, setPassedApplicants] = useState([]);
    const [selectedApplicants, setSelectedApplicants] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectAll, setSelectAll] = useState(false);

    const schoolId = localStorage.getItem("school_id");

    // ✅ Fetch Interview List
    const fetchInterviews = async () => {
        try {
            const res = await api.get(`/interview/list?school_id=${schoolId}`);
            const data = res.data;
            if (data?.status) setInterviews(data.data || []);
            else setInterviews([]);
        } catch (err) {
            console.error("Error fetching interviews:", err);
            setInterviews([]);
        }
    };

    // ✅ Fetch Passed Applicants (for scheduling)
    const fetchPassedApplicants = async () => {
        try {
            setLoading(true);
            const res = await api.get(`/exam_result/list?exam_id=1&school_id=${schoolId}`);
            if (res.data?.status) {
                const passed = res.data.data.filter((x) => x.result_status === "passed");
                setPassedApplicants(passed);
            }
        } catch (err) {
            console.error("Error fetching applicants:", err);
        } finally {
            setLoading(false);
        }
    };

    // ✅ Schedule Interview
    const handleSchedule = async () => {
        if (!form.interview_date || !form.venue || selectedApplicants.length === 0) {
            setMsg({ type: "error", text: "Please fill all required fields." });
            return;
        }

        try {
            const payload = {
                application_ids: selectedApplicants,
                interview_date: form.interview_date,
                interview_time: `${form.start_time} - ${form.end_time}`,
                venue: form.venue,
            };
            const res = await api.post("/interview/schedule", payload);
            if (res.data.status) {
                setMsg({ type: "success", text: res.data.message });
                setScheduleOpen(false);
                fetchInterviews();
            } else {
                setMsg({ type: "error", text: res.data.message });
            }
        } catch (err) {
            console.error("Error scheduling interview:", err);
            setMsg({ type: "error", text: "Network error" });
        }
    };

    // ✅ Update Interview Result
    const handleUpdateResult = async (id, result, remarks) => {
        try {
            const payload = { id, result, remarks };
            const res = await api.post("/interview/update_status", payload);
            if (res.data.status) {
                setMsg({ type: "success", text: "Interview result updated!" });
                fetchInterviews();
            }
        } catch (err) {
            console.error(err);
            setMsg({ type: "error", text: "Failed to update result." });
        }
    };

    // ✅ View Interview Details
    const handleView = async (application_id) => {
        try {
            const res = await api.get(`/interview/view/${application_id}`);
            if (res.data?.status) setSelectedInterview(res.data.data);
            setOpen(true);
        } catch (err) {
            console.error(err);
            setMsg({ type: "error", text: "Failed to load interview details." });
        }
    };

    // ✅ Select All (Only Paid)
    const handleSelectAll = (checked) => {
        setSelectAll(checked);
        if (checked) {
            const paidIds = filteredApplicants
                .filter((a) => a.payment_status === "paid")
                .map((a) => a.application_id);
            setSelectedApplicants(paidIds);
        } else {
            setSelectedApplicants([]);
        }
    };

    // ✅ Filter by search query
    const filteredApplicants = passedApplicants.filter((a) =>
        a.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    useEffect(() => {
        fetchInterviews();
    }, []);

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                }}
            >
                <Typography variant="h5" fontWeight={600}>
                    🗓️ Parent Interview Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {
                        fetchPassedApplicants();
                        setScheduleOpen(true);
                    }}
                >
                    Schedule Interview
                </Button>
            </Box>

            {/* Interview List */}
            <Paper sx={{ mt: 3 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Applicant Name</TableCell>
                            <TableCell>Class</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Time</TableCell>
                            <TableCell>Venue</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Result</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {interviews.map((i) => (
                            <TableRow key={i.id}>
                                <TableCell>{i.name}</TableCell>
                                <TableCell>{i.class_applied}</TableCell>
                                <TableCell>{i.interview_date}</TableCell>
                                <TableCell>{i.interview_time || "--"}</TableCell>
                                <TableCell>{i.venue}</TableCell>
                                <TableCell>
                                    <Typography color={i.status === "completed" ? "green" : "orange"}>
                                        {i.status}
                                    </Typography>
                                </TableCell>
                                <TableCell>{i.result}</TableCell>
                                <TableCell>
                                    <IconButton color="primary" onClick={() => handleView(i.application_id)}>
                                        <Visibility />
                                    </IconButton>
                                    <IconButton
                                        color="success"
                                        onClick={() => handleUpdateResult(i.id, "selected", "Good performance")}
                                    >
                                        <Done />
                                    </IconButton>
                                    <IconButton
                                        color="error"
                                        onClick={() => handleUpdateResult(i.id, "rejected", "Not qualified")}
                                    >
                                        <Schedule />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            {/* Schedule Interview Dialog */}
            <Dialog
                open={scheduleOpen}
                onClose={() => setScheduleOpen(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ fontWeight: 600 }}>📅 Schedule Parent Interviews</DialogTitle>
                <DialogContent>
                    {loading ? (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                            <CircularProgress />
                            <Typography>Loading passed applicants...</Typography>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                                <TextField
                                    label="Interview Date"
                                    type="date"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={form.interview_date}
                                    onChange={(e) =>
                                        setForm({ ...form, interview_date: e.target.value })
                                    }
                                />
                                <TextField
                                    label="Start Time"
                                    type="time"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={form.start_time || ""}
                                    onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                                />
                                <Typography variant="body1" sx={{ mt: 1 }}>to</Typography>
                                <TextField
                                    label="End Time"
                                    type="time"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={form.end_time || ""}
                                    onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                                />
                            </Box>

                            <TextField
                                label="Venue"
                                fullWidth
                                margin="dense"
                                value={form.venue}
                                onChange={(e) => setForm({ ...form, venue: e.target.value })}
                            />

                            {/* Filter and Select All */}
                            <Box
                                sx={{
                                    mt: 2,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <TextField
                                    placeholder="Search applicant..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    size="small"
                                    sx={{ width: "60%" }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Checkbox
                                        checked={selectAll}
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                    />
                                    <Typography>Select All (Only Paid)</Typography>
                                </Box>
                            </Box>

                            {/* Applicants List */}
                            <Box
                                sx={{
                                    maxHeight: 300,
                                    overflowY: "auto",
                                    border: "1px solid #ddd",
                                    borderRadius: 2,
                                    mt: 2,
                                }}
                            >
                                {filteredApplicants.length > 0 ? (
                                    filteredApplicants.map((a) => (
                                        <Box
                                            key={a.application_id}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                px: 2,
                                                py: 1,
                                                borderBottom: "1px solid #eee",
                                            }}
                                        >
                                            <Checkbox
                                                checked={selectedApplicants.includes(a.application_id)}
                                                onChange={(e) => {
                                                    if (e.target.checked)
                                                        setSelectedApplicants([
                                                            ...selectedApplicants,
                                                            a.application_id,
                                                        ]);
                                                    else
                                                        setSelectedApplicants(
                                                            selectedApplicants.filter(
                                                                (id) => id !== a.application_id
                                                            )
                                                        );
                                                }}
                                                disabled={a.payment_status !== "paid"}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography sx={{ fontWeight: 500 }}>
                                                    {a.name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {a.class_applied} — {a.payment_status === "paid" ? "Paid" : "Unpaid"}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography sx={{ p: 2 }}>No applicants found.</Typography>
                                )}
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setScheduleOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSchedule}>
                        Schedule
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Interview Details */}
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Interview Details</DialogTitle>
                <DialogContent>
                    {selectedInterview ? (
                        <>
                            <Typography>Name: {selectedInterview.name}</Typography>
                            <Typography>Class: {selectedInterview.class_applied}</Typography>
                            <Typography>Date: {selectedInterview.interview_date}</Typography>
                            <Typography>Time: {selectedInterview.interview_time}</Typography>
                            <Typography>Venue: {selectedInterview.venue}</Typography>
                            <Typography>Result: {selectedInterview.result}</Typography>
                            <Typography>Remarks: {selectedInterview.remarks}</Typography>
                        </>
                    ) : (
                        <Typography>No data found.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={!!msg}
                autoHideDuration={3000}
                onClose={() => setMsg(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                {msg && (
                    <Alert severity={msg.type} sx={{ width: "100%" }}>
                        {msg.text}
                    </Alert>
                )}
            </Snackbar>
        </Box>
    );
}
